// src/modules/nlp/NERLab.jsx
// NER Lab — Rule-based · Pattern matching · BIO Tagging · Entity Types · Annotation · Coreference · Evaluation
// Tabs: input | extract | annotate | bio | patterns | coref | evaluation | concepts

import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import { extractEntities, tokenizeWords, tokenizeSentences, SAMPLE_TEXTS } from '../../utils/nlpUtils.js';
import { Panel, InfoBox } from '../../components/ui.jsx';

const ACCENT = 'var(--orange)';
import { _livePalette as P } from '../../context/paletteSync';
const TABS = ['input','extract','annotate','bio','patterns','coref','evaluation','concepts'];

const Btn = ({onClick,children,color=ACCENT,disabled,style})=>(
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Tag = ({children,color=ACCENT})=>(
  <span style={{padding:'2px 8px',background:color+'20',border:`1px solid ${color}44`,color,fontSize:'9px',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>{children}</span>
);
const MC = ({label,value,sub,color=ACCENT})=>(
  <div style={{background:'var(--raised)',border:`1px solid ${color}22`,borderRadius:'3px',padding:'12px 14px'}}>
    <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>
    <div style={{fontSize:'18px',fontWeight:700,fontFamily:'Syne',color}}>{value}</div>
    {sub&&<div style={{fontSize:'10px',color:'var(--t3)',marginTop:'2px'}}>{sub}</div>}
  </div>
);

const TYPE_META = {
  PERSON:{ color:'var(--cyan)',   icon:'👤', label:'PERSON',   desc:'People by name' },
  ORG:   { color:'var(--violet)', icon:'🏢', label:'ORG',      desc:'Companies, institutions' },
  LOC:   { color:'var(--green)',  icon:'📍', label:'LOCATION', desc:'Places, cities, countries' },
  NUM:   { color:'var(--gold)',   icon:'#',  label:'NUMBER',   desc:'Currency, percentages' },
  TIME:  { color:'var(--orange)', icon:'🕐', label:'TIME',     desc:'Dates, months, years' },
};

// BIO tagger: tags tokens B-TYPE, I-TYPE, O
function bioTag(text, nerResult) {
  const words   = text.split(/\s+/);
  const tagged  = words.map(w => ({ word:w, tag:'O', type:null }));
  nerResult.entities.forEach(ent => {
    const entWords = ent.text.split(' ');
    for (let i=0; i<words.length; i++) {
      if (words[i].replace(/[^a-zA-Z0-9$%.-]/g,'') === entWords[0].replace(/[^a-zA-Z0-9$%.-]/g,'')) {
        let match = true;
        for (let j=1; j<entWords.length; j++) {
          if (!words[i+j] || words[i+j].replace(/[^a-zA-Z0-9$%.-]/g,'') !== entWords[j].replace(/[^a-zA-Z0-9$%.-]/g,'')) {
            match=false; break;
          }
        }
        if (match) {
          tagged[i]   = { word:words[i],   tag:`B-${ent.type}`, type:ent.type };
          for (let j=1; j<entWords.length; j++) {
            if (tagged[i+j]) tagged[i+j] = { word:words[i+j], tag:`I-${ent.type}`, type:ent.type };
          }
        }
      }
    }
  });
  return tagged;
}

// Simple coreference resolver (rule-based pronouns)
function resolveCoref(text) {
  const sentences = tokenizeSentences(text);
  const chains    = [];
  let lastPerson  = null;
  let lastOrg     = null;
  let lastLoc     = null;

  const result = sentences.map(sent => {
    const words   = sent.split(/\s+/);
    const nerR    = extractEntities(sent);
    if (nerR.byType.PERSON.length) lastPerson = nerR.byType.PERSON[0].text;
    if (nerR.byType.ORG.length)    lastOrg    = nerR.byType.ORG[0].text;
    if (nerR.byType.LOC.length)    lastLoc    = nerR.byType.LOC[0].text;

    const resolved = [];
    words.forEach(w => {
      const l = w.toLowerCase().replace(/[^a-z]/g,'');
      if (['he','him','his'].includes(l) && lastPerson)  resolved.push({word:w, ref:lastPerson, type:'PERSON'});
      else if (['she','her','hers'].includes(l) && lastPerson) resolved.push({word:w, ref:lastPerson, type:'PERSON'});
      else if (['they','them','their'].includes(l) && (lastOrg||lastPerson)) resolved.push({word:w, ref:lastOrg||lastPerson, type:'ORG'});
      else if (['it','its'].includes(l) && lastOrg) resolved.push({word:w, ref:lastOrg, type:'ORG'});
      else if (['there'].includes(l) && lastLoc) resolved.push({word:w, ref:lastLoc, type:'LOC'});
    });
    return { sent, resolved };
  });
  return result;
}

export default function NERLab() {
  const [tab, setTab]     = useState('input');
  const [text, setText]   = useState(SAMPLE_TEXTS.mixed);
  const [result, setResult] = useState(null);
  const [bioTokens, setBioTokens] = useState([]);
  const [corefResult, setCorefResult] = useState([]);
  const [customPatterns, setCustomPatterns] = useState('');
  const [patternText, setPatternText]       = useState('Apple Inc. released the iPhone 15 in Cupertino, California on September 12, 2023. CEO Tim Cook presented the device to analysts.');

  const runNER = () => {
    const r = extractEntities(text);
    setResult(r);
    setBioTokens(bioTag(text, r));
    setCorefResult(resolveCoref(text));
  };

  const runPattern = () => {
    const r = extractEntities(patternText);
    setBioTokens(bioTag(patternText, r));
    setResult(r);
  };

  const countsByType = result ? Object.entries(TYPE_META).map(([type,meta])=>({
    type, label:meta.label, count:result.counts[type]||0, color:meta.color
  })) : [];

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      {/* Header */}
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?ACCENT:'transparent'}`,background:tab===t?ACCENT+'18':'transparent',color:tab===t?ACCENT:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>
        ))}
      </div>

      {/* ── INPUT ── */}
      {tab==='input' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="INPUT TEXT" accent={ACCENT}>
            <textarea value={text} onChange={e=>{setText(e.target.value);setResult(null);}} style={{width:'100%',minHeight:'160px',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',resize:'vertical',lineHeight:1.7,boxSizing:'border-box'}}/>
            <div style={{display:'flex',gap:'8px',marginTop:'10px',flexWrap:'wrap'}}>
              {Object.entries(SAMPLE_TEXTS).map(([key])=>(
                <button key={key} onClick={()=>{setText(SAMPLE_TEXTS[key]);setResult(null);}} style={{padding:'5px 12px',border:'1px solid var(--b1)',background:'var(--raised)',color:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=ACCENT} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b1)'}>{key}</button>
              ))}
              <Btn onClick={()=>{runNER();setTab('extract');}} color={ACCENT}>EXTRACT ENTITIES →</Btn>
            </div>
          </Panel>
          <InfoBox color={ACCENT} icon="⬡">
            <strong>Best sample:</strong> "mixed" contains Dr. Sarah Johnson (PERSON), MIT & Amazon (ORG), $2.5 billion & 3.5% (NUM), Tuesday March 15 (TIME), New York & San Francisco (LOC). Run NER to see all entities extracted.
          </InfoBox>
          {/* Entity type guide */}
          <Panel title="ENTITY TYPES — WHAT WE DETECT" accent={ACCENT}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'10px'}}>
              {Object.entries(TYPE_META).map(([type,{color,icon,label,desc}])=>(
                <div key={type} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}44`,borderRadius:'4px',textAlign:'center'}}>
                  <div style={{fontSize:'22px',marginBottom:'6px'}}>{icon}</div>
                  <div style={{fontSize:'11px',fontWeight:700,color,marginBottom:'4px'}}>{label}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)'}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── EXTRACT ── */}
      {tab==='extract' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {!result && <div style={{padding:'20px',textAlign:'center'}}><Btn onClick={runNER} color={ACCENT}>EXTRACT ENTITIES →</Btn></div>}
          {result && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'10px'}}>
                {Object.entries(TYPE_META).map(([type,{color,label}])=>(
                  <MC key={type} label={label} value={result.counts[type]||0} color={color}/>
                ))}
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                {/* Entity count chart */}
                <Panel title="ENTITY TYPE DISTRIBUTION" accent={ACCENT}>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={countsByType}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="label" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Bar dataKey="count" radius={[3,3,0,0]}>
                        {countsByType.map((d,i)=><Cell key={i} fill={d.color} opacity={.85}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>

                {/* Entity list per type */}
                <Panel title="EXTRACTED ENTITIES" accent={ACCENT}>
                  <div style={{maxHeight:'260px',overflowY:'auto'}}>
                    {Object.entries(TYPE_META).map(([type,{color,icon,label}])=>{
                      const ents = result.byType[type];
                      if (!ents?.length) return null;
                      return (
                        <div key={type} style={{marginBottom:'12px'}}>
                          <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'6px'}}>{icon} {label}</div>
                          <div style={{display:'flex',flexWrap:'wrap',gap:'5px'}}>
                            {ents.map((e,i)=>(
                              <span key={i} style={{display:'inline-flex',alignItems:'center',gap:'4px',padding:'3px 10px',background:color+'18',border:`1px solid ${color}44`,borderRadius:'3px',fontSize:'11px'}}>
                                <span style={{color:'var(--t3)',fontSize:'8px'}}>{type}</span>
                                <span style={{color,fontFamily:'JetBrains Mono',fontWeight:600}}>{e.text}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Panel>
              </div>

              {/* Entity-in-context */}
              <Panel title="ENTITIES IN CONTEXT — Sentence View" accent={ACCENT}>
                <div style={{maxHeight:'300px',overflowY:'auto'}}>
                  {tokenizeSentences(text).map((sent,si)=>{
                    const sentEnts = result.entities.filter(e=>sent.includes(e.text));
                    if (!sentEnts.length) return (
                      <div key={si} style={{padding:'8px 12px',marginBottom:'4px',borderBottom:'1px solid var(--b0)',fontSize:'11px',color:'var(--t3)'}}>
                        <span style={{fontSize:'9px',marginRight:'8px',color:'var(--b2)'}}>#{si+1}</span>{sent}
                      </div>
                    );
                    return (
                      <div key={si} style={{padding:'10px 14px',marginBottom:'4px',background:'var(--raised)',borderRadius:'3px',border:'1px solid var(--b0)'}}>
                        <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'6px'}}>SENTENCE #{si+1}</div>
                        <div style={{fontSize:'11px',color:'var(--t2)',lineHeight:2.0,marginBottom:'8px'}}>
                          {(()=>{
                            let remaining = sent;
                            const sortedEnts = [...sentEnts].sort((a,b)=>sent.indexOf(a.text)-sent.indexOf(b.text));
                            const els = [];
                            sortedEnts.forEach((ent,j)=>{
                              const idx = remaining.indexOf(ent.text);
                              if (idx<0) return;
                              if (idx>0) els.push(<span key={`t${j}`}>{remaining.slice(0,idx)}</span>);
                              const c = TYPE_META[ent.type]?.color||'var(--t2)';
                              els.push(
                                <span key={`e${j}`} style={{background:c+'20',borderBottom:`2px solid ${c}`,padding:'1px 3px',borderRadius:'2px',color:c,fontWeight:700,position:'relative'}} title={ent.type}>
                                  {ent.text}
                                  <sup style={{fontSize:'7px',color:c,marginLeft:'1px'}}>{ent.type}</sup>
                                </span>
                              );
                              remaining = remaining.slice(idx+ent.text.length);
                            });
                            if (remaining) els.push(<span key="tail">{remaining}</span>);
                            return els;
                          })()}
                        </div>
                        <div style={{display:'flex',gap:'5px',flexWrap:'wrap'}}>
                          {sentEnts.map((e,j)=>(
                            <span key={j} style={{padding:'2px 8px',background:TYPE_META[e.type]?.color+'18',border:`1px solid ${TYPE_META[e.type]?.color}44`,color:TYPE_META[e.type]?.color,fontSize:'9px',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{e.type}: {e.text}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── ANNOTATE ── */}
      {tab==='annotate' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {!result && <div style={{padding:'20px',textAlign:'center'}}><Btn onClick={runNER} color={ACCENT}>RUN NER FIRST →</Btn></div>}
          {result && (
            <>
              <Panel title="ANNOTATED TEXT — Full document view" accent={ACCENT}>
                <div style={{marginBottom:'12px',display:'flex',gap:'8px',flexWrap:'wrap'}}>
                  {Object.entries(TYPE_META).map(([type,{color,icon,label}])=>(
                    <span key={type} style={{display:'inline-flex',alignItems:'center',gap:'5px',padding:'4px 10px',background:color+'15',border:`1px solid ${color}44`,borderRadius:'3px',fontSize:'10px'}}>
                      <span style={{width:'8px',height:'8px',background:color,borderRadius:'1px',flexShrink:0}}/>
                      <span style={{color}}>{label}</span>
                    </span>
                  ))}
                </div>
                <div style={{fontSize:'13px',lineHeight:2.4,color:'var(--t2)',fontFamily:'JetBrains Mono',padding:'14px',background:'var(--deep)',borderRadius:'3px',border:'1px solid var(--b0)'}}>
                  {(()=>{
                    const sortedEnts = [...result.entities].sort((a,b)=>{
                      const ia = text.indexOf(a.text), ib = text.indexOf(b.text);
                      return ia - ib;
                    });
                    let remaining = text;
                    const els = [];
                    sortedEnts.forEach((ent,j)=>{
                      const idx = remaining.indexOf(ent.text);
                      if (idx<0) return;
                      if (idx>0) els.push(<span key={`t${j}`}>{remaining.slice(0,idx)}</span>);
                      const c = TYPE_META[ent.type]?.color||'var(--t2)';
                      els.push(
                        <span key={`e${j}`} title={ent.type} style={{background:c+'22',borderBottom:`2px solid ${c}`,padding:'2px 4px',margin:'0 1px',borderRadius:'3px',color:c,fontWeight:600,cursor:'help'}}>
                          {ent.text}
                          <span style={{fontSize:'8px',background:c,color:'var(--void)',borderRadius:'2px',padding:'0px 4px',marginLeft:'3px',fontWeight:700,verticalAlign:'middle'}}>{ent.type}</span>
                        </span>
                      );
                      remaining = remaining.slice(idx+ent.text.length);
                    });
                    if (remaining) els.push(<span key="tail">{remaining}</span>);
                    return els;
                  })()}
                </div>
              </Panel>

              {/* Stats summary */}
              <Panel title="ANNOTATION STATISTICS" accent={ACCENT}>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
                  <MC label="Total entities"  value={result.entities.length}                    color={ACCENT}/>
                  <MC label="Entity density"  value={+(result.entities.length/tokenizeWords(text).length*100).toFixed(1)+'%'} sub="entities/words" color="var(--cyan)"/>
                  <MC label="Unique entities" value={new Set(result.entities.map(e=>e.text.toLowerCase())).size} color="var(--violet)"/>
                  <MC label="Avg ent/sent"    value={+(result.entities.length/tokenizeSentences(text).length).toFixed(1)} color="var(--gold)"/>
                  <MC label="Multi-word ents" value={result.entities.filter(e=>e.text.includes(' ')).length} color="var(--green)"/>
                  <MC label="Single-word"     value={result.entities.filter(e=>!e.text.includes(' ')).length} color="var(--orange)"/>
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── BIO TAGGING ── */}
      {tab==='bio' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="BIO TAGGING SCHEME" accent="var(--cyan)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'14px',lineHeight:1.7}}>
              The BIO (Begin-Inside-Outside) scheme converts NER into a sequence labeling task. Every token gets a tag: <strong style={{color:'var(--green)'}}>B-TYPE</strong> (first token of entity), <strong style={{color:'var(--cyan)'}}>I-TYPE</strong> (continuation), <strong style={{color:'var(--t3)'}}>O</strong> (outside/no entity). Enables training with CRF or BiLSTM-CRF.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px',marginBottom:'14px'}}>
              {[
                {tag:'B-TYPE',color:'var(--green)',example:'B-PERSON',desc:'Beginning of entity span. "Dr." in "Dr. Smith" → B-PERSON'},
                {tag:'I-TYPE',color:'var(--cyan)',example:'I-PERSON',desc:'Inside (continuation). "Smith" in "Dr. Smith" → I-PERSON'},
                {tag:'O',color:'var(--t3)',example:'O',desc:'Outside — not part of any entity. "said", "the", "in" → O'},
              ].map(({tag,color,example,desc})=>(
                <div key={tag} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}44`,borderRadius:'4px'}}>
                  <code style={{fontSize:'14px',fontWeight:700,color,fontFamily:'JetBrains Mono'}}>{tag}</code>
                  <div style={{fontSize:'11px',color:'var(--t2)',marginTop:'4px',marginBottom:'6px'}}>{example}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>

          {!result && <div style={{padding:'20px',textAlign:'center'}}><Btn onClick={runNER} color="var(--cyan)">GENERATE BIO TAGS →</Btn></div>}

          {bioTokens.length > 0 && (
            <>
              <Panel title="TOKEN-LEVEL BIO TAG SEQUENCE" accent="var(--cyan)">
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',maxHeight:'300px',overflowY:'auto',padding:'10px',background:'var(--deep)',borderRadius:'3px',border:'1px solid var(--b0)'}}>
                  {bioTokens.map(({word,tag,type},i)=>{
                    const c = tag==='O'?'var(--t3)':tag.startsWith('B-')?TYPE_META[type]?.color||'var(--cyan)':'var(--t2)';
                    const bg= tag==='O'?'var(--raised)':tag.startsWith('B-')?(TYPE_META[type]?.color||'var(--cyan)')+'25':'(TYPE_META[type]?.color||"var(--cyan)")+"12"';
                    return (
                      <div key={i} style={{textAlign:'center',minWidth:'50px'}}>
                        <div style={{fontSize:'11px',color:'var(--t1)',fontFamily:'JetBrains Mono',padding:'3px 6px',background:'var(--raised)',borderRadius:'2px 2px 0 0',border:'1px solid var(--b0)',borderBottom:'none'}}>{word.replace(/[^a-zA-Z0-9$%.,]/g,'').slice(0,10)||word.slice(0,10)}</div>
                        <div style={{fontSize:'8px',padding:'2px 4px',background:tag==='O'?'var(--deep)':TYPE_META[type]?.color+'30'||'var(--cyan)30',color:c,borderRadius:'0 0 2px 2px',border:`1px solid ${tag==='O'?'var(--b0)':TYPE_META[type]?.color||'var(--cyan)'}`,fontFamily:'JetBrains Mono',fontWeight:tag!=='O'?700:400}}>{tag}</div>
                      </div>
                    );
                  })}
                </div>
              </Panel>

              {/* BIO variants */}
              <Panel title="BIO VARIANTS" accent="var(--cyan)">
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                  {[
                    {scheme:'IOB1',tags:'B,I,O',desc:'Original IOB. B- only used when same type as previous token. Less common.'},
                    {scheme:'IOB2 (BIO)',tags:'B,I,O',desc:'B- always starts new entity. Most widely used. CoNLL-2003 standard. Our implementation.'},
                    {scheme:'BIOES',tags:'B,I,O,E,S',desc:'Adds E- (End) and S- (Single). More information. Better performance on some models.'},
                    {scheme:'BILOU',tags:'B,I,L,O,U',desc:'Begin, Inside, Last, Outside, Unit. Same as BIOES. Used by spaCy. Good for single-word entities.'},
                  ].map(({scheme,tags,desc})=>(
                    <div key={scheme} style={{padding:'10px 12px',background:'var(--raised)',border:'1px solid var(--cyan)33',borderRadius:'3px'}}>
                      <div style={{fontSize:'12px',fontWeight:700,color:'var(--cyan)',marginBottom:'4px',fontFamily:'JetBrains Mono'}}>{scheme}</div>
                      <div style={{fontSize:'10px',color:'var(--gold)',marginBottom:'6px',fontFamily:'JetBrains Mono'}}>{tags}</div>
                      <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.55}}>{desc}</div>
                    </div>
                  ))}
                </div>
              </Panel>

              {/* Tag distribution */}
              <Panel title="TAG DISTRIBUTION" accent="var(--cyan)">
                {(()=>{
                  const tagFreq = {};
                  bioTokens.forEach(({tag})=>{tagFreq[tag]=(tagFreq[tag]||0)+1;});
                  const data = Object.entries(tagFreq).sort((a,b)=>b[1]-a[1]).map(([tag,count])=>({tag,count}));
                  return (
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="tag" stroke="var(--t3)" tick={{fontSize:8}} angle={-20} textAnchor="end" height={48} interval={0}/>
                        <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                        <Tooltip/>
                        <Bar dataKey="count" radius={[2,2,0,0]}>
                          {data.map(({tag},i)=>{
                            const c = tag==='O'?'var(--t3)':tag.startsWith('B-')?'var(--green)':'var(--cyan)';
                            return <Cell key={i} fill={c} opacity={.85}/>;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  );
                })()}
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── PATTERNS ── */}
      {tab==='patterns' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="PATTERN-BASED NER — RULES & HEURISTICS" accent="var(--violet)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              Our rule-based NER uses lexical and syntactic patterns. Test on the example sentence or write your own. Click patterns to understand the logic behind each rule.
            </div>
            <div style={{marginBottom:'10px'}}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.1em'}}>TEST TEXT</div>
              <textarea value={patternText} onChange={e=>setPatternText(e.target.value)} style={{width:'100%',minHeight:'80px',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'10px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',resize:'vertical',lineHeight:1.7,boxSizing:'border-box',marginBottom:'8px'}}/>
              <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                <Btn onClick={runPattern} color="var(--violet)">EXTRACT →</Btn>
                {['Apple Inc. released iPhone in Cupertino on September 12. CEO Tim Cook attended.','Dr. Sarah Johnson earned $2.5M at Google LLC in New York in 2023.','The MIT professor and Amazon VP met in San Francisco on Monday.'].map(ex=>(
                  <button key={ex.slice(0,20)} onClick={()=>setPatternText(ex)} style={{padding:'5px 10px',border:'1px solid var(--b0)',background:'var(--raised)',color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',overflow:'hidden',maxWidth:'180px',textOverflow:'ellipsis'}}
                    onMouseEnter={e=>e.currentTarget.style.color='var(--violet)'} onMouseLeave={e=>e.currentTarget.style.color='var(--t3)'}>{ex.slice(0,30)}…</button>
                ))}
              </div>
            </div>
          </Panel>

          {/* Pattern rules reference */}
          <Panel title="PATTERN RULES — HOW EACH ENTITY TYPE IS DETECTED" accent="var(--violet)">
            <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
              {[
                {type:'PERSON',color:'var(--cyan)',patterns:[
                  {rule:'Title + Cap word','example':'Dr. Smith, Prof. Chen, Mr. Johnson','regex':'TITLES.has(prev) && /^[A-Z]/.test(curr)'},
                  {rule:'Two consecutive Cap words','example':'Sarah Johnson, Tim Cook, Andy Jassy','regex':'/^[A-Z][a-z]+$/.test(w1) && /^[A-Z][a-z]+$/.test(w2)'},
                ]},
                {type:'ORG',color:'var(--violet)',patterns:[
                  {rule:'ALL CAPS acronym (2-5 letters)','example':'MIT, IBM, NASA, FBI, WHO','regex':'/^[A-Z]{2,5}$/.test(word)'},
                  {rule:'Cap word + org suffix','example':'Google Inc, Apple Corp, OpenAI Ltd','regex':'/^[A-Z]/.test(word) && ORG_SFX.has(next)'},
                ]},
                {type:'LOC',color:'var(--green)',patterns:[
                  {rule:'Cap word after location preposition','example':'"in New York", "at Paris", "from London"','regex':'["in","at","from"].includes(prev) && /^[A-Z][a-z]+$/.test(word)'},
                ]},
                {type:'NUM',color:'var(--gold)',patterns:[
                  {rule:'Currency amount','example':'$2.5M, $100, $1,000,000','regex':'/^\\$[\\d,]+(\\.\\d+)?$/.test(word)'},
                  {rule:'Percentage','example':'3.5%, 100%, 0.5%','regex':'/^\\d+(\\.\\d+)?%$/.test(word)'},
                  {rule:'Plain number','example':'2023, 100, 1,500','regex':'/^\\d{1,3}(,\\d{3})*(\\.\\d+)?$/.test(word)'},
                ]},
                {type:'TIME',color:'var(--orange)',patterns:[
                  {rule:'Month name','example':'January, March, September','regex':'MONTHS.has(word.toLowerCase())'},
                  {rule:'Weekday','example':'Monday, Tuesday, Friday','regex':'WEEKDAYS.has(word.toLowerCase())'},
                  {rule:'4-digit year','example':'2023, 2024, 1999','regex':'/(19|20)\\d{2}/.test(word)'},
                ]},
              ].map(({type,color,patterns})=>(
                <div key={type} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color,marginBottom:'8px'}}>{type}</div>
                  {patterns.map(({rule,example,regex})=>(
                    <div key={rule} style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'8px',marginBottom:'6px',padding:'6px 8px',background:'var(--deep)',borderRadius:'2px'}}>
                      <div style={{fontSize:'10px',color:'var(--t2)',fontWeight:600}}>{rule}</div>
                      <div style={{fontSize:'10px',color:'var(--t3)',fontStyle:'italic'}}>{example}</div>
                      <code style={{fontSize:'9px',color:'var(--code)',fontFamily:'JetBrains Mono',wordBreak:'break-all'}}>{regex}</code>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── COREFERENCE ── */}
      {tab==='coref' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="COREFERENCE RESOLUTION — PRONOUN TRACKING" accent="var(--green)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              Coreference resolution identifies when different expressions refer to the same entity. "Dr. Smith arrived. <strong style={{color:'var(--cyan)'}}>She</strong> presented her findings." → "She" = "Dr. Smith". Our rule-based approach tracks the most recent named entity of each type and resolves pronouns.
            </div>
            {!result && <Btn onClick={()=>{runNER();}} color="var(--green)">RUN NER FIRST →</Btn>}
          </Panel>

          {corefResult.length > 0 && (
            <>
              <Panel title="SENTENCE-LEVEL COREFERENCE CHAINS" accent="var(--green)">
                <div style={{maxHeight:'350px',overflowY:'auto'}}>
                  {corefResult.map(({sent,resolved},i)=>(
                    <div key={i} style={{padding:'10px 14px',borderBottom:'1px solid var(--b0)',marginBottom:'2px'}}>
                      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'6px'}}>SENTENCE #{i+1}</div>
                      <div style={{fontSize:'11px',color:'var(--t2)',lineHeight:1.7,marginBottom:resolved.length?'8px':'0'}}>
                        {sent.split(/\s+/).map((word,j)=>{
                          const ref = resolved.find(r=>r.word.replace(/[^a-zA-Z]/g,'').toLowerCase()===word.replace(/[^a-zA-Z]/g,'').toLowerCase());
                          if (ref) {
                            const c = TYPE_META[ref.type]?.color||'var(--green)';
                            return <span key={j}><span style={{background:c+'25',borderBottom:`2px solid ${c}`,padding:'1px 3px',borderRadius:'2px',color:c,fontWeight:700,cursor:'help'}} title={`refers to: ${ref.ref}`}>{word}</span> </span>;
                          }
                          return <span key={j}>{word} </span>;
                        })}
                      </div>
                      {resolved.length > 0 && (
                        <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                          {resolved.map((r,k)=>(
                            <div key={k} style={{fontSize:'10px',display:'inline-flex',alignItems:'center',gap:'5px',padding:'3px 8px',background:'var(--ggreen)',border:'1px solid var(--green)33',borderRadius:'2px'}}>
                              <span style={{color:'var(--green)',fontFamily:'JetBrains Mono',fontWeight:700}}>{r.word}</span>
                              <span style={{color:'var(--t3)'}}>→</span>
                              <span style={{color:'var(--cyan)',fontFamily:'JetBrains Mono'}}>{r.ref}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Panel>

              <Panel title="COREFERENCE APPROACHES" accent="var(--green)">
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
                  {[
                    ['Pronoun Rules (ours)','var(--green)','Track most recent entity per type. Map he/him/his → last PERSON. Works for simple texts. Fails on: ambiguous antecedents, long-distance reference, complex syntax.'],
                    ['Hobbs Algorithm (1978)','var(--gold)','Syntax-tree based. Parse tree search for antecedents. Number/gender agreement constraints. Better than pronoun rules but still brittle. Requires full parse tree.'],
                    ['Neural Coref (2017)','var(--violet)','Clark & Manning: span ranking with neural mention detection. End-to-end: no parse tree needed. AllenNLP implementation. F1≈67% on CoNLL-2012.'],
                    ['SpanBERT (2019)','var(--cyan)','Fine-tuned SpanBERT on coreference resolution. Learns span representations. F1≈79% on CoNLL-2012 — near human performance (~87%). Hugging Face: coreferee library.'],
                    ['GPT-4 (zero-shot)','#34d399','LLMs handle coreference well in context windows. "Who does \'he\' refer to?" → often correct. No specialized training. Useful for complex cases that fool specialized models.'],
                    ['OntoNotes benchmark','var(--orange)','Standard: CoNLL-2012 shared task. OntoNotes corpus: 1.6M words, 6 languages. Includes broadcast news, telephone conversations, web data. Gold coreference annotations.'],
                  ].map(([title,color,desc])=>(
                    <div key={title} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                      <div style={{color,fontWeight:700,fontSize:'11px',marginBottom:'7px'}}>{title}</div>
                      <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.65}}>{desc}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── EVALUATION ── */}
      {tab==='evaluation' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="NER EVALUATION METRICS" accent="var(--red)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'14px',lineHeight:1.7}}>
              NER is evaluated at entity level: an entity is correct only if <strong>both</strong> the span (exact boundaries) and the type match the gold label. Partial spans count as false positives AND false negatives.
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px',marginBottom:'14px'}}>
              {[
                {metric:'Precision',formula:'TP / (TP + FP)',color:'var(--cyan)',desc:'Of all entities the model predicted, what fraction was correct? High precision = few false alarms.'},
                {metric:'Recall',formula:'TP / (TP + FN)',color:'var(--green)',desc:'Of all true entities in the text, what fraction did the model find? High recall = few misses.'},
                {metric:'F1 Score',formula:'2×P×R / (P+R)',color:'var(--gold)',desc:'Harmonic mean of precision and recall. Standard NER benchmark metric. Macro-F1 averages across entity types.'},
              ].map(({metric,formula,color,desc})=>(
                <div key={metric} style={{padding:'14px',background:'var(--raised)',border:`1px solid ${color}44`,borderRadius:'4px'}}>
                  <div style={{fontSize:'14px',fontWeight:700,color,marginBottom:'4px',fontFamily:'Syne'}}>{metric}</div>
                  <div style={{padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',fontFamily:'JetBrains Mono',fontSize:'11px',color:'var(--code)',marginBottom:'8px'}}>{formula}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>

            {/* Worked example */}
            <div style={{padding:'14px',background:'var(--raised)',border:'1px solid var(--b1)',borderRadius:'4px',marginBottom:'14px'}}>
              <div style={{fontSize:'10px',fontWeight:700,color:'var(--t2)',marginBottom:'10px',letterSpacing:'.06em'}}>WORKED EXAMPLE</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px',fontSize:'10px',fontFamily:'JetBrains Mono'}}>
                {[
                  {col:'Gold (correct)',items:['PERSON: Dr. Smith','ORG: MIT','LOC: Boston','TIME: 2023','NUM: $50M'],color:'var(--green)'},
                  {col:'Predicted',items:['PERSON: Dr. Smith ✓','PERSON: MIT ✗ (wrong type)','— (missed Boston)','TIME: 2023 ✓','NUM: $50M ✓'],color:'var(--cyan)'},
                  {col:'Result',items:['TP: Dr. Smith','FP: MIT (wrong type)','FN: Boston (missed)','TP: 2023','TP: $50M'],color:'var(--gold)'},
                ].map(({col,items,color})=>(
                  <div key={col}>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'6px',letterSpacing:'.08em'}}>{col}</div>
                    {items.map((item,i)=><div key={i} style={{padding:'3px 0',color,borderBottom:'1px solid var(--b0)'}}>{item}</div>)}
                  </div>
                ))}
              </div>
              <div style={{marginTop:'10px',fontSize:'10px',color:'var(--t3)'}}>
                TP=3, FP=1, FN=1 → Precision=3/4=<span style={{color:'var(--cyan)',fontWeight:700}}>0.75</span>, Recall=3/5=<span style={{color:'var(--green)',fontWeight:700}}>0.60</span>, F1=<span style={{color:'var(--gold)',fontWeight:700}}>0.667</span>
              </div>
            </div>
          </Panel>

          {/* Model benchmarks */}
          <Panel title="NER MODEL BENCHMARKS — CoNLL-2003 English F1" accent="var(--red)">
            {(()=>{
              const models=[
                {model:'Rule-based',per:71,org:67,loc:74,misc:65,overall:69,color:'var(--t3)'},
                {model:'CRF (hand features)',per:85,org:80,loc:84,misc:76,overall:82,color:'var(--gold)'},
                {model:'BiLSTM-CRF',per:90,org:85,loc:90,misc:78,overall:87,color:'var(--green)'},
                {model:'ELMo + CRF',per:92,org:88,loc:92,misc:82,overall:90,color:'var(--cyan)'},
                {model:'BERT-base',per:95,org:88,loc:93,misc:82,overall:92,color:'var(--violet)'},
                {model:'RoBERTa-large',per:96,org:91,loc:95,misc:85,overall:93,color:'#f472b6'},
                {model:'GPT-NER (2023)',per:97,org:93,loc:96,misc:88,overall:95,color:'#34d399'},
              ];
              return (
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                    <thead><tr style={{background:'var(--deep)'}}>
                      {['Model','PER','ORG','LOC','MISC','Overall F1'].map(h=>(
                        <th key={h} style={{padding:'8px 12px',textAlign:h==='Model'?'left':'center',color:'var(--t3)',fontSize:'9px',letterSpacing:'.08em',borderBottom:'1px solid var(--b0)'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {models.map(({model,per,org,loc,misc,overall,color},i)=>(
                        <tr key={model} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.04)'}}>
                          <td style={{padding:'8px 12px',color,fontWeight:600,fontFamily:'JetBrains Mono'}}>{model}</td>
                          {[per,org,loc,misc,overall].map((v,j)=>(
                            <td key={j} style={{padding:'8px 12px',textAlign:'center',color:v>=90?'var(--green)':v>=80?'var(--gold)':'var(--t3)',fontFamily:'JetBrains Mono',fontWeight:j===4?700:400}}>{v}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </Panel>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab==='concepts' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {[
              {title:'NER in the NLP Pipeline',color:ACCENT,desc:'NER is typically a mid-pipeline task: tokenize → POS tag → chunking/parsing → NER → relation extraction → event extraction. Entities are building blocks for knowledge graphs, QA, information extraction. SpaCy\'s pipeline: tok2vec → tagger → parser → NER → other components.'},
              {title:'Nested & Overlapping Entities',color:'var(--violet)',desc:'"Bank of China" contains "China" (LOC) inside "Bank of China" (ORG). Standard BIO cannot represent nesting. Solutions: cascade NER (process outer first), hypergraph CRF, span-based models (enumerate all spans). ACE dataset has nested entities.'},
              {title:'Low-resource NER',color:'var(--cyan)',desc:'CoNLL-2003 has 14K training sentences. But many languages/domains lack labeled data. Solutions: (1) Cross-lingual transfer from English; (2) Few-shot with GPT-4; (3) Distant supervision from Wikidata/Wikipedia; (4) Data augmentation: synonym replacement, back-translation.'},
              {title:'Domain Adaptation',color:'var(--green)',desc:'General NER fails on specialized domains. Clinical: diseases, medications, symptoms (i2b2 corpus). Scientific: chemicals, genes, proteins (BioCreative). Legal: case names, statutes, courts. Finance: tickers, fund names. Solution: domain-specific pretraining (BioBERT, SciBERT, LegalBERT).'},
              {title:'Relation Extraction',color:'var(--gold)',desc:'Once entities are found, extract relations between them: (Apple Inc, CEO, Tim Cook), (Steve Jobs, founded, Apple). Standard dataset: ACE 2004/2005, TACRED. Joint NER + RE: PURE, SpERT. Knowledge graph population: Wikidata is semi-automatically built this way.'},
              {title:'OntoNotes Entity Types',color:'var(--red)',desc:'The 18 OntoNotes entity types used by SpaCy and Stanford NER: PERSON, NORP (nationalities/religious), FAC (facilities), ORG, GPE (geopolitical), LOC, PRODUCT, EVENT, WORK_OF_ART, LAW, LANGUAGE, DATE, TIME, PERCENT, MONEY, QUANTITY, ORDINAL, CARDINAL. CoNLL uses simpler 4-type scheme.'},
            ].map(({title,color,desc})=>(
              <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
